<template>
  <v-card-text>
    <v-autocomplete
      v-model="localForm.beneficiaryIntake"
      v-model:search-input="searchBeneficiaries"
      :loading="isLoading"
      return-object
      placeholder="Start typing to Search"
      item-title="name"
      item-value="id"
      :items="beneficiaries"
      variant="outlined"
      hide-details="auto"
      :menu-props="{ maxWidth: 0 }"
      :error-messages="getErrorMessages('beneficiaryIntake').value"
      @update:search="(val) => onInput(val)"
    >
      <template #label>
        Beneficiary
        <span v-if="isRequired('beneficiaryIntake')" class="ml-1 red--text">
          <strong> *</strong>
        </span>
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-model="localForm.completedCourse"
      class="pt-5"
      return-object
      item-title="name"
      item-value="id"
      :items="courses"
      variant="outlined"
      hide-details="auto"
      :menu-props="{ maxWidth: 0 }"
    >
      <template #label>
        What is the training you completed at Odyssea?
      </template>
    </v-autocomplete>

    <v-select
      v-model="localForm.jobFoundMethod"
      class="pt-5"
      :items="jobFoundMethods"
      item-title="name"
      return-object
      variant="outlined"
      hide-details="auto"
      :error-messages="getErrorMessages('jobFoundMethod').value"
      :is-required="isRequired('jobFoundMethod')"
      @blur="v$.jobFoundMethod?.$touch()"
      @update:model-value="updateForm"
    >
      <template #label>
        How did you find the job?
        <span v-if="isRequired('jobFoundMethod')" class="ml-1 red--text">
          <strong> *</strong>
        </span>
      </template>
    </v-select>

    <v-select
      v-model="localForm.didOdysseaSupport"
      class="pt-5"
      :items="booleanOptions"
      item-title="text"
      item-value="value"
      hide-details="auto"
      variant="outlined"
      :error-messages="getErrorMessages('didOdysseaSupport').value"
      :is-required="isRequired('didOdysseaSupport')"
      @blur="v$.didOdysseaSupport?.$touch()"
      @update:model-value="updateForm"
    >
      <template #label>
        Did Odyssea support him/her in finding employment?
        <span v-if="isRequired('didOdysseaSupport')" class="ml-1 red--text">
          <strong> *</strong>
        </span>
      </template>
    </v-select>

    <v-date-input
      :model-value="localForm.date"
      class="pt-5"
      variant="outlined"
      prepend-icon=""
      placeholder="DD/MM/YYYY"
      prepend-inner-icon="$calendar"
      hide-details="auto"
      @update:model-value="(val) => { localForm.date = toLocalDateString(val); updateForm(); }"
    >
      <template #label> Date </template>
    </v-date-input>
  </v-card-text>
</template>

<script setup>
import { computed, watch, reactive, ref, onMounted } from "vue";
import useVuelidate from "@vuelidate/core";
import { defineProps, defineEmits } from "vue";
import { useListStore } from "@/stores";

const listStore = useListStore();
const courses = computed(() => listStore.courses);

const props = defineProps({
  form: Object,
  rules: Object,
});

const emit = defineEmits(["update:form"]);

const localForm = reactive(props.form);

const requiredFields = ["beneficiaryIntake", "jobFoundMethod"];

const completionPercent = computed(() => {
  const total = requiredFields.length;

  const filled = requiredFields.filter((field) => {
    return (
      localForm[field] !== null &&
      localForm[field] !== undefined &&
      localForm[field] !== ""
    );
  }).length;

  return Math.round((filled / total) * 100);
});

const searchBeneficiaries = ref(null);
const beneficiaries = ref([]);
const isLoading = ref(false);

const onInput = async (val) => {
  if (val != null && val.length > 2) {
    isLoading.value = true;
    try {
      beneficiaries.value = await listStore.fetchBeneficiaryIntakes({
        keyword: val,
      });
    } catch (error) {
      console.error("Error fetching beneficiary intakes:", error);
      beneficiaries.value = [];
    } finally {
      isLoading.value = false;
    }
  } else {
    beneficiaries.value = [];
    isLoading.value = false;
  }
};

const isRequired = (field) => requiredFields.includes(field);

const v$ = useVuelidate(props.rules, localForm);

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : [],
  );

const jobFoundMethods = [
  { name: "Route 1: I found the job myself", value: "ROUTE_1" },
  { name: "Route 2: Opportunity communicated by Odyssea", value: "ROUTE_2" },
  { name: "Route 3: Interview arranged by Odyssea", value: "ROUTE_3" },
  { name: "Route 4: Upskilling/reskilling", value: "ROUTE_4" },
  { name: "Other", value: "OTHER" },
];

const booleanOptions = [
  { text: "Yes", value: true },
  { text: "No", value: false },
];

const toLocalDateString = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const updateForm = () => {
  emit("update:form", { ...localForm });
};

watch(
  () => props.form,
  (newVal) => {
    Object.assign(localForm, newVal);
  },
  { deep: true, immediate: true },
);

watch(
  localForm,
  (val) => {
    emit("update:form", val);
  },
  { deep: true },
);

watch(
  completionPercent,
  (val) => {
    emit("update:completion", val);
  },
  { immediate: true },
);

onMounted(async () => {
  await listStore.fetchCourses();
});
</script>

<style scoped>
.text-grey {
  color: rgba(0, 0, 0, 0.6);
  font-style: italic;
}
</style>
